/**
 * CorporateSummaryPanel — top-level orchestrator for the hierarchical corporate summary.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import {
  EntityDetailsCard,
  type CorporateEntityDetail,
} from "./EntityDetailsCard";
import {
  DirectorInvolvementPanel,
  type DirectorOtherInvolvement,
} from "./DirectorInvolvementPanel";

interface CorporateSummaryPanelProps {
  jobId: Id<"jobs">;
  isRunning: boolean;
  onViewDocument: (docId: string) => void;
  /** Personal-insolvency jobs drop "Corporate" from the summary card header. */
  isBankruptcy?: boolean;
}

export function CorporateSummaryPanel({
  jobId,
  isRunning,
  onViewDocument,
  isBankruptcy = false,
}: CorporateSummaryPanelProps) {
  const [drillEntity, setDrillEntity] = useState<CorporateEntityDetail | null>(
    null,
  );
  const [drillLabel, setDrillLabel] = useState("");

  const { data } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getCorporateHierarchicalSummary,
      { jobId },
    ),
  );

  if (!data) {
    if (isRunning) {
      return (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
          {isBankruptcy
            ? "Building summary..."
            : "Building corporate summary..."}
        </div>
      );
    }
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {isBankruptcy
          ? "No data available yet."
          : "No corporate data available yet."}
      </p>
    );
  }

  const summary = data as {
    entityInLiquidation: CorporateEntityDetail | null;
    holdingCompany: CorporateEntityDetail | null;
    subsidiaryCompanies: CorporateEntityDetail[];
    sisterCompanies: CorporateEntityDetail[];
    directorsOtherInvolvement: DirectorOtherInvolvement[];
    documentsAnalysed: number;
    totalEntities: number;
  };

  const entity = summary.entityInLiquidation;

  const handleRelatedEntityClick = (
    relatedEntity: CorporateEntityDetail,
    label: string,
  ) => {
    setDrillEntity(relatedEntity);
    setDrillLabel(label);
  };

  if (drillEntity) {
    return (
      <div className="flex flex-col gap-8 pb-8 pr-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDrillEntity(null)}
            className="-ml-2 mb-3 gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to {drillLabel}
          </Button>
          <EntityDetailsCard
            entity={drillEntity}
            onViewDocument={onViewDocument}
            isBankruptcy={isBankruptcy}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 pb-8 pr-4">
      {entity && (
        <EntityDetailsCard
          entity={entity}
          onViewDocument={onViewDocument}
          holdingCompany={summary.holdingCompany}
          subsidiaries={summary.subsidiaryCompanies}
          sisterCompanies={summary.sisterCompanies}
          onRelatedEntityClick={handleRelatedEntityClick}
          isBankruptcy={isBankruptcy}
        />
      )}

      {/* Directors' Other Involvement — only when at least one director has involvements */}
      {summary.directorsOtherInvolvement.some(
        (d: { involvements: unknown[] }) => d.involvements.length > 0,
      ) && (
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Directors' Other Involvement
          </h3>
          <DirectorInvolvementPanel
            directors={summary.directorsOtherInvolvement}
            onViewDocument={onViewDocument}
          />
        </div>
      )}

      {/* Footer */}
      <p className="text-[10px] tabular-nums text-muted-foreground">
        {summary.totalEntities} entities from {summary.documentsAnalysed}{" "}
        documents
      </p>
    </div>
  );
}
